/* eslint-disable @typescript-eslint/no-explicit-any, no-console */

import * as Sentry from "@sentry/browser";
import { Severity } from "@sentry/browser";
import { Vue as VueSentryIntegration } from "@sentry/integrations/dist/vue";

import Vue from "vue";

import { LogLevel, LogLevelLabel, LogLevels } from "@/types";

export interface NjBreadcrumb {
  data?: any;
  category: string;
  message: string;
  level: LogLevelLabel;
  type: string;
}

/**
 * Custom logging service that utilizes local state
 *
 * TODO: debug mode
 */
export class LogService {
  private static _initialized = false;
  private static _isSentryEnabled = false;
  private static _isSentryLoaded = false;
  private static _sentryLogLevel: LogLevel = LogLevels.None;
  private static _consoleLogLevel: LogLevel = LogLevels.None;
  private static _preInitQueue: { logLevel: LogLevel; messageList: any[] }[] =
    [];

  static set consoleLogLevel(logLevel: LogLevel) {
    this._consoleLogLevel = logLevel;
    this.debug(`LogService.consoleLogLevel updated to '${logLevel.label}'`);
  }

  // TODO: remove when deleting /debug page
  static get consoleLogLevel(): LogLevel {
    return this._consoleLogLevel;
  }

  static set sentryLogLevel(sentryLogLevel: LogLevel) {
    this._sentryLogLevel = sentryLogLevel;
    this.debug(
      `LogService.sentryLogLevel updated to '${sentryLogLevel.label}'`
    );
  }

  // TODO: remove when deleting /debug page
  static get sentryLogLevel(): LogLevel {
    return this._sentryLogLevel;
  }

  static get isSentryLoaded(): boolean {
    return this._isSentryLoaded;
  }

  static _initialize(): void {
    if (this._initialized) {
      return;
    }
    this.sentryLogLevel = LogLevels.fromString(
      process.env.VUE_APP_SENTRY_LOG_LEVEL || "error"
    );
    this.consoleLogLevel = LogLevels.fromString(
      process.env.VUE_APP_CONSOLE_LOG_LEVEL || "error"
    );

    this.debug("Initializing LogService.");

    // Initialize Sentry if a valid DSN is provided
    this._isSentryEnabled = (process.env.VUE_APP_SENTRY_SDK_DSN || "").includes(
      "http"
    );
    if (this._isSentryEnabled) {
      Sentry.init({
        dsn: process.env.VUE_APP_SENTRY_SDK_DSN,
        environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,
        integrations: [new VueSentryIntegration({ Vue, attachProps: true })],
      });
      this._isSentryLoaded = true;
      this.info("Sentry is initialized.");
    }

    this._initialized = true;
    this._purgePreInitQueue();
    this.info("LogService is initialized.");
  }

  private static _purgePreInitQueue(): void {
    this.debug(`LogService._purgePreInitQueue started.`);
    for (const logItem of this._preInitQueue) {
      this._log(logItem.logLevel, logItem.messageList);
    }
    this.debug(`LogService._purgePreInitQueue is complete.`);
  }

  private static _log(logLevel: LogLevel, ...messageList: any[]): void {
    if (!this._initialized) {
      this._preInitQueue.push({ logLevel, messageList });
      return;
    }

    const message: string = messageList.join(" ");
    const dateObj = new Date();
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString();
    const dateTime = `[${date} - ${time}]`;

    // If enabled, log to console
    if (
      LogLevels.greaterThan(logLevel, LogLevels.None) &&
      LogLevels.lessThanOrEqual(logLevel, this._consoleLogLevel)
    ) {
      switch (logLevel) {
        case LogLevels.Fatal:
          console.error(dateTime, "Fatal:", message);
          break;
        case LogLevels.Error:
          console.error(dateTime, "Error:", message);
          break;
        case LogLevels.Warn:
          console.warn(dateTime, "Warning:", message);
          break;
        case LogLevels.Info:
          console.info(dateTime, "Info:", message);
          break;
        case LogLevels.Debug:
          console.debug(dateTime, "Debug:", message);
          break;
        case LogLevels.Trace:
          console.trace(dateTime, "Trace:", message);
          break;
        default:
      }
    }

    // If enabled, log to Sentry
    if (
      this._isSentryLoaded &&
      LogLevels.greaterThan(logLevel, LogLevels.None) &&
      LogLevels.lessThanOrEqual(logLevel, this._sentryLogLevel)
    ) {
      Sentry.captureMessage(
        message,
        // All LogLevels string values are supported by Sentry, except Trace (doesn't exist).
        Severity.fromString(
          LogLevels.equals(logLevel, LogLevels.Trace) ? "debug" : logLevel.label
        )
      );
    }
  }

  // ----- PUBLIC FUNCTIONS ----- //

  public static fatal(...messageList: any[]): void {
    this._log(LogLevels.Fatal, messageList);
  }

  public static error(...messageList: any[]): void {
    this._log(LogLevels.Error, messageList);
  }

  public static warn(...messageList: any[]): void {
    this._log(LogLevels.Warn, messageList);
  }

  public static info(...messageList: any[]): void {
    this._log(LogLevels.Info, messageList);
  }

  public static log(...messageList: any[]): void {
    this._log(LogLevels.Info, messageList);
  }

  public static debug(...messageList: any[]): void {
    this._log(LogLevels.Debug, messageList);
  }

  public static trace(...messageList: any[]): void {
    this._log(LogLevels.Trace, messageList);
  }

  // TODO: capture and log exceptions
  public static throwException(error?: Error): void {
    throw new Error("Function not yet implemented...");
    // Sentry.captureException(new Error("Message!"));
    Sentry.captureException(error);
  }

  public static addBreadcrumb(breadcrumb: NjBreadcrumb): void {
    const { level } = breadcrumb;
    const l = Severity.fromString(level);
    Sentry.addBreadcrumb({ ...breadcrumb, level: l });
  }
}
